import { useCallback, useMemo, useState } from "react";

/**
 * Sunucunun `firms[]` satırını seçici seçeneğine çevirir. Tip ve ağaç
 * bilgisi korunur: kök bayi ise liste bayi ağıdır (alt bayiler + müşteriler,
 * #200) ve FirmDropdown tipe göre ikon, derinliğe göre girinti basar.
 */
export const toScopeFirmOption = (f) => ({
  _id: f._id,
  firmName: f.firmName,
  firmType: f.firmType,
  parentId: f.parentId,
  depth: f.depth,
});

/**
 * FİRMA KAPSAM SEÇİMİ — firma detayındaki "hangi firmalar dahil" seçicisi.
 *
 * VARSAYILAN: ana firma + TÜM alt firmaları (kullanıcı kararı 2026-09-14).
 * Önceki varsayılan yalnızca firmanın kendisiydi; ana firmaya giren kullanıcı
 * şubelerinin rakamlarını görmüyor, gördüğünü sanıyordu.
 *
 * SUNUCU SÖZLEŞMESİ: `firmIds` BOŞ gönderilirse sunucu kapsamı "kök + tüm alt
 * firmalar" olarak çözer (bkz. `resolveConsumptionScope` / `resolveBillScope`).
 * Bu yüzden "tümü" hâli, firma id listesi doldurularak değil BOŞ dizi
 * gönderilerek anlatılır — ilk istek de doğru kapsamla gider, listeyi sonradan
 * düzeltmek için ikinci bir tur atılmaz.
 *
 * `selection === null` → dokunulmamış = TÜMÜ.
 * Kullanıcı seçimi tamamen temizlerse yine "tümü"ne dönülür: boş kapsam hiçbir
 * şey göstermez, kullanıcı bunu istemiş olamaz.
 *
 * @param {Array<{_id: string, firmName: string}>} firmOptions
 *   sunucudan gelen kök + alt firma listesi (istek dönene kadar boş olabilir)
 */
export default function useFirmScopeSelection(firmOptions = []) {
  const [selection, setSelection] = useState(null);

  /** İsteğe gidecek değer: boş dizi = sunucu tüm ağacı alsın. */
  const requestFirmIds = useMemo(
    () => (Array.isArray(selection) && selection.length > 0 ? selection : []),
    [selection],
  );

  /** Seçicide işaretli görünecek firmalar — "tümü" hâlinde hepsi işaretli. */
  const selectedFirms = useMemo(() => {
    if (!Array.isArray(selection)) return firmOptions;
    return firmOptions.filter((f) => selection.includes(f._id));
  }, [selection, firmOptions]);

  const onChange = useCallback((next) => {
    const ids = (next || []).map((f) => f._id);
    // Hepsi seçiliyse ya da hiçbiri seçili değilse "tümü" hâline dön: ikisi de
    // aynı kapsamı anlatır, sunucuya kalabalık id listesi göndermeye gerek yok.
    setSelection(ids.length > 0 ? ids : null);
  }, []);

  /** Alt firması olmayan firmada seçici gösterilmez. */
  const hasSubFirms = firmOptions.length > 1;

  return { requestFirmIds, selectedFirms, onChange, hasSubFirms, selection };
}
